/**
 * Teen Patti Engine
 *
 * Core game logic: table setup, boot collection, dealing, Blind/Seen mechanics,
 * betting, Sideshow, Showdown, pot limit, player elimination, dealer rotation.
 */

import {
  Card, TeenPattiHandResult, TeenPattiPlayerState, BlindSeenStatus,
  TeenPattiAction, ValidTeenPattiAction, ActionResult, TeenPattiActionRecord,
  SideshowRequest, SideshowResult, ShowdownResult, ShowdownPlayerResult,
  TeenPattiGameState,
  VisibleTeenPattiState, VisibleTeenPattiPlayerState,
  TeenPattiGameConfig, DEFAULT_BOOT_AMOUNT, DEFAULT_BUY_IN,
} from './types';
import { createDeck, shuffleDeck, dealToPlayers } from './Deck';
import * as HandEvaluator from './TeenPattiHandEvaluator';

export class TeenPattiEngine {
  private state: TeenPattiGameState;

  constructor() {
    this.state = this.createEmptyState();
  }

  private createEmptyState(): TeenPattiGameState {
    return {
      tableSize: 0, buyIn: DEFAULT_BUY_IN, bootAmount: DEFAULT_BOOT_AMOUNT,
      potLimit: null, players: [], dealerSeatIndex: -1, roundNumber: 0,
      deck: { cards: [], dealtCards: [] }, pot: 0, currentStake: 0,
      currentPlayerIndex: -1, actionHistory: [], pendingSideshow: null,
      gameStatus: { type: 'waiting' }, roundHistory: [],
    };
  }

  initializeGame(config: TeenPattiGameConfig): void {
    const { tableSize, buyIn, bootAmount, potLimit, players: playerConfigs } = config;
    if (playerConfigs.length < 3 || playerConfigs.length > 6) {
      throw new Error('Teen Patti requires 3-6 players');
    }
    const players: TeenPattiPlayerState[] = playerConfigs.map((pc, i) => ({
      id: pc.id, name: pc.name, seatIndex: i, chipStack: buyIn,
      cards: [], status: 'active', blindSeenStatus: 'blind' as BlindSeenStatus,
      currentBetThisRound: 0, isDealer: false, isHuman: pc.isHuman,
      aiDifficulty: pc.aiDifficulty, hasActedThisRound: false,
    }));
    const dealerIdx = Math.floor(Math.random() * players.length);
    players[dealerIdx].isDealer = true;
    this.state = {
      ...this.createEmptyState(), tableSize: tableSize || playerConfigs.length,
      buyIn: buyIn || DEFAULT_BUY_IN, bootAmount: bootAmount || DEFAULT_BOOT_AMOUNT,
      potLimit: potLimit ?? null, players, dealerSeatIndex: dealerIdx,
      gameStatus: { type: 'active' },
    };
  }

  startNewRound(): void {
    const s = this.state;
    // Eliminate players who can't afford boot
    for (const p of s.players) {
      if (p.status === 'active' && p.chipStack < s.bootAmount) {
        p.status = 'eliminated';
      }
    }
    const active = s.players.filter(p => p.status === 'active');
    if (active.length < 2) {
      if (active.length === 1) {
        s.gameStatus = { type: 'complete', winnerId: active[0].id };
      }
      return;
    }
    // Rotate dealer
    this.rotateDealer();
    // Reset round state
    s.roundNumber++;
    s.pot = 0;
    s.currentStake = s.bootAmount;
    s.actionHistory = [];
    s.pendingSideshow = null;
    s.gameStatus = { type: 'roundInProgress' };
    // Reset player round state and collect boot
    for (const p of s.players) {
      p.cards = [];
      p.currentBetThisRound = 0;
      p.hasActedThisRound = false;
      p.blindSeenStatus = 'blind';
      if (p.status === 'active') {
        p.chipStack -= s.bootAmount;
        p.currentBetThisRound = s.bootAmount;
        s.pot += s.bootAmount;
      }
    }
    // Shuffle and deal
    let deck = createDeck();
    deck = shuffleDeck(deck);
    const activeIndices = s.players.map((p, i) => p.status === 'active' ? i : -1).filter(i => i >= 0);
    const { playerHands, deck: newDeck } = dealToPlayers(deck, activeIndices.length);
    s.deck = newDeck;
    activeIndices.forEach((pi, hi) => { s.players[pi].cards = playerHands[hi]; });
    // Set current player to left of dealer
    s.currentPlayerIndex = this.getNextActiveIndex(s.dealerSeatIndex);
  }

  private rotateDealer(): void {
    const s = this.state;
    for (const p of s.players) p.isDealer = false;
    const nextDealer = this.getNextActiveIndex(s.dealerSeatIndex);
    s.dealerSeatIndex = nextDealer;
    s.players[nextDealer].isDealer = true;
  }

  private getNextActiveIndex(fromIndex: number): number {
    const len = this.state.players.length;
    for (let i = 1; i <= len; i++) {
      const idx = (fromIndex + i) % len;
      if (this.state.players[idx].status === 'active') return idx;
    }
    return fromIndex;
  }

  private getActivePlayersInRound(): TeenPattiPlayerState[] {
    return this.state.players.filter(p => p.status === 'active');
  }

  private advanceToNextPlayer(): void {
    this.state.currentPlayerIndex = this.getNextActiveIndex(this.state.currentPlayerIndex);
  }

  // =========================================================================
  // Blind/Seen mechanics
  // =========================================================================

  seeCards(playerId: string): Card[] {
    const player = this.findPlayer(playerId);
    if (!player || player.status !== 'active') throw new Error('Player not found or not active');
    if (player.blindSeenStatus === 'seen') return player.cards;
    player.blindSeenStatus = 'seen';
    return player.cards;
  }

  isPlayerBlind(playerId: string): boolean {
    const p = this.findPlayer(playerId);
    return p ? p.blindSeenStatus === 'blind' : false;
  }

  isPlayerSeen(playerId: string): boolean {
    const p = this.findPlayer(playerId);
    return p ? p.blindSeenStatus === 'seen' : false;
  }

  // =========================================================================
  // Valid actions
  // =========================================================================

  getValidActions(playerId: string): ValidTeenPattiAction[] {
    const s = this.state;
    const player = this.findPlayer(playerId);
    if (!player || player.status !== 'active') return [];
    if (s.gameStatus.type !== 'roundInProgress') return [];
    if (s.players[s.currentPlayerIndex]?.id !== playerId) return [];
    if (s.pendingSideshow) return [];

    const actions: ValidTeenPattiAction[] = [];
    const isBlind = player.blindSeenStatus === 'blind';
    const stake = s.currentStake;
    const minBet = isBlind ? stake : stake * 2;
    const maxBet = isBlind ? stake * 2 : stake * 4;

    // Pack is always available
    actions.push({ type: 'pack' });

    // Call
    if (player.chipStack >= minBet) {
      actions.push({ type: 'call', callAmount: minBet });
    }

    // Raise
    if (player.chipStack >= minBet) {
      actions.push({
        type: 'raise',
        minAmount: minBet,
        maxAmount: Math.min(maxBet, player.chipStack),
      });
    }

    // See Cards (only if Blind)
    if (isBlind) {
      actions.push({ type: 'see-cards' });
    }

    // Sideshow (only if Seen, previous player was Seen, 3+ active players)
    const activePlayers = this.getActivePlayersInRound();
    if (!isBlind && activePlayers.length >= 3) {
      const prevIdx = this.getPreviousActiveIndex(s.currentPlayerIndex);
      const prevPlayer = s.players[prevIdx];
      if (prevPlayer && prevPlayer.blindSeenStatus === 'seen' && prevPlayer.status === 'active') {
        actions.push({ type: 'sideshow' });
      }
    }

    // Show (only if exactly 2 active players remain)
    if (activePlayers.length === 2) {
      actions.push({ type: 'show' });
    }

    return actions;
  }

  private getPreviousActiveIndex(fromIndex: number): number {
    const len = this.state.players.length;
    for (let i = 1; i <= len; i++) {
      const idx = (fromIndex - i + len) % len;
      if (this.state.players[idx].status === 'active') return idx;
    }
    return fromIndex;
  }

  // =========================================================================
  // Submit action
  // =========================================================================

  submitAction(playerId: string, action: TeenPattiAction): ActionResult {
    const s = this.state;
    const player = this.findPlayer(playerId);
    if (!player) return { success: false, error: 'Player not found' };
    if (player.status !== 'active') return { success: false, error: 'Player is not active' };
    if (s.gameStatus.type !== 'roundInProgress') return { success: false, error: 'No round in progress' };
    if (s.players[s.currentPlayerIndex]?.id !== playerId) return { success: false, error: "It's not your turn" };
    if (s.pendingSideshow) return { success: false, error: 'Sideshow pending' };

    const validActions = this.getValidActions(playerId);
    const isValid = validActions.some(va => va.type === action.type);
    if (!isValid) return { success: false, error: `Action '${action.type}' is not available` };

    switch (action.type) {
      case 'pack': return this.handlePack(player);
      case 'call': return this.handleCall(player);
      case 'raise': return this.handleRaise(player, action.amount || 0);
      case 'see-cards': return this.handleSeeCards(player);
      case 'sideshow': return this.handleSideshowAction(player);
      case 'show': return this.handleShow(player);
      default: return { success: false, error: 'Unknown action' };
    }
  }

  private handlePack(player: TeenPattiPlayerState): ActionResult {
    player.status = 'packed';
    const record = this.recordAction(player, 'pack', 0);
    const active = this.getActivePlayersInRound();
    if (active.length === 1) {
      return this.endRoundByPack(active[0], record);
    }
    this.advanceToNextPlayer();
    return { success: true, action: record };
  }

  private handleCall(player: TeenPattiPlayerState): ActionResult {
    const isBlind = player.blindSeenStatus === 'blind';
    const amount = isBlind ? this.state.currentStake : this.state.currentStake * 2;
    if (player.chipStack < amount) return { success: false, error: 'Not enough chips' };
    player.chipStack -= amount;
    player.currentBetThisRound += amount;
    this.state.pot += amount;
    player.hasActedThisRound = true;
    const record = this.recordAction(player, 'call', amount);
    this.checkPotLimit();
    if (this.state.gameStatus.type !== 'roundInProgress') {
      return { success: true, action: record, roundComplete: true, showdown: this.state.roundHistory[this.state.roundHistory.length - 1] ? undefined : undefined };
    }
    this.advanceToNextPlayer();
    return { success: true, action: record };
  }

  private handleRaise(player: TeenPattiPlayerState, amount: number): ActionResult {
    const isBlind = player.blindSeenStatus === 'blind';
    const stake = this.state.currentStake;
    const minBet = isBlind ? stake : stake * 2;
    const maxBet = isBlind ? stake * 2 : stake * 4;
    const clampedAmount = Math.max(minBet, Math.min(maxBet, amount, player.chipStack));
    if (player.chipStack < clampedAmount) return { success: false, error: 'Not enough chips' };
    player.chipStack -= clampedAmount;
    player.currentBetThisRound += clampedAmount;
    this.state.pot += clampedAmount;
    // Update current stake
    if (isBlind) {
      this.state.currentStake = clampedAmount;
    } else {
      this.state.currentStake = Math.floor(clampedAmount / 2);
    }
    player.hasActedThisRound = true;
    const record = this.recordAction(player, 'raise', clampedAmount);
    this.checkPotLimit();
    if (this.state.gameStatus.type !== 'roundInProgress') {
      return { success: true, action: record, roundComplete: true };
    }
    this.advanceToNextPlayer();
    return { success: true, action: record };
  }

  private handleSeeCards(player: TeenPattiPlayerState): ActionResult {
    player.blindSeenStatus = 'seen';
    const record = this.recordAction(player, 'see-cards', 0);
    // Don't advance turn - player still needs to bet
    return { success: true, action: record };
  }

  private handleSideshowAction(player: TeenPattiPlayerState): ActionResult {
    const prevIdx = this.getPreviousActiveIndex(this.state.currentPlayerIndex);
    const target = this.state.players[prevIdx];
    if (!target || target.blindSeenStatus !== 'seen') {
      return { success: false, error: 'Invalid sideshow target' };
    }
    // Player must pay call amount to request sideshow
    const callAmount = this.state.currentStake * 2;
    if (player.chipStack < callAmount) return { success: false, error: 'Not enough chips' };
    player.chipStack -= callAmount;
    player.currentBetThisRound += callAmount;
    this.state.pot += callAmount;
    player.hasActedThisRound = true;
    this.state.pendingSideshow = { requesterId: player.id, targetId: target.id, pending: true };
    this.state.gameStatus = { type: 'sideshowPending', requesterId: player.id, targetId: target.id };
    const record = this.recordAction(player, 'sideshow', callAmount);
    return { success: true, action: record, sideshowPending: true };
  }

  private handleShow(player: TeenPattiPlayerState): ActionResult {
    const active = this.getActivePlayersInRound();
    if (active.length !== 2) return { success: false, error: 'Show requires exactly 2 players' };
    // Player must pay appropriate bet
    const isBlind = player.blindSeenStatus === 'blind';
    const amount = isBlind ? this.state.currentStake : this.state.currentStake * 2;
    if (player.chipStack < amount) return { success: false, error: 'Not enough chips' };
    player.chipStack -= amount;
    player.currentBetThisRound += amount;
    this.state.pot += amount;
    const record = this.recordAction(player, 'show', amount);
    const showdown = this.resolveShowdown(false, player.id);
    return { success: true, action: record, roundComplete: true, showdown };
  }

  // =========================================================================
  // Sideshow resolution
  // =========================================================================

  requestSideshow(requesterId: string): SideshowRequest {
    const player = this.findPlayer(requesterId);
    if (!player || player.blindSeenStatus !== 'seen') throw new Error('Requester must be Seen');
    const active = this.getActivePlayersInRound();
    if (active.length < 3) throw new Error('Sideshow requires 3+ players');
    const prevIdx = this.getPreviousActiveIndex(this.state.players.indexOf(player));
    const target = this.state.players[prevIdx];
    if (!target || target.blindSeenStatus !== 'seen') throw new Error('Target must be Seen');
    const req: SideshowRequest = { requesterId, targetId: target.id, pending: true };
    this.state.pendingSideshow = req;
    return req;
  }

  respondToSideshow(targetId: string, accept: boolean): SideshowResult {
    const s = this.state;
    const req = s.pendingSideshow;
    if (!req || req.targetId !== targetId) throw new Error('No pending sideshow for this player');
    s.pendingSideshow = null;
    s.gameStatus = { type: 'roundInProgress' };
    if (!accept) {
      const result: SideshowResult = { requesterId: req.requesterId, targetId, accepted: false };
      // Requester must continue, advance turn
      this.advanceToNextPlayer();
      return result;
    }
    // Compare hands privately
    const requester = this.findPlayer(req.requesterId)!;
    const target = this.findPlayer(targetId)!;
    const hand1 = HandEvaluator.evaluate(requester.cards);
    const hand2 = HandEvaluator.evaluate(target.cards);
    const cmp = HandEvaluator.compare(hand1, hand2);
    let loserId: string;
    if (cmp > 0) {
      loserId = targetId;
      target.status = 'packed';
    } else if (cmp < 0) {
      loserId = req.requesterId;
      requester.status = 'packed';
    } else {
      // Equal hands: requester packs
      loserId = req.requesterId;
      requester.status = 'packed';
    }
    const result: SideshowResult = { requesterId: req.requesterId, targetId, accepted: true, loserId };
    // Check if round ends
    const active = this.getActivePlayersInRound();
    if (active.length === 1) {
      this.endRoundByPack(active[0]);
    } else {
      this.advanceToNextPlayer();
    }
    return result;
  }

  // =========================================================================
  // Showdown
  // =========================================================================

  requestShowdown(playerId: string): ShowdownResult {
    const active = this.getActivePlayersInRound();
    if (active.length !== 2) throw new Error('Showdown requires exactly 2 players');
    return this.resolveShowdown(false, playerId);
  }

  private resolveShowdown(isForced: boolean, requesterId?: string): ShowdownResult {
    const active = this.getActivePlayersInRound();
    const playerResults: ShowdownPlayerResult[] = active.map(p => {
      const handResult = HandEvaluator.evaluate(p.cards);
      return { playerId: p.id, playerName: p.name, cards: [...p.cards], handResult };
    });
    // Find best hand
    let bestIdx = 0;
    for (let i = 1; i < playerResults.length; i++) {
      const cmp = HandEvaluator.compare(playerResults[i].handResult, playerResults[bestIdx].handResult);
      if (cmp > 0) {
        bestIdx = i;
      } else if (cmp === 0 && !isForced && requesterId) {
        // Equal hands at showdown: pot goes to player who did NOT request
        if (playerResults[bestIdx].playerId === requesterId) {
          bestIdx = i;
        }
      }
    }
    const winnerId = playerResults[bestIdx].playerId;
    const potAmount = this.state.pot;
    // Award pot
    const winner = this.findPlayer(winnerId)!;
    winner.chipStack += potAmount;
    const showdown: ShowdownResult = { playerResults, winnerId, potAmount, isForcedShowdown: isForced };
    // Record round summary
    this.state.roundHistory.push({
      roundNumber: this.state.roundNumber, winnerId, winnerName: winner.name,
      winningHand: playerResults[bestIdx].handResult, potAmount,
      playerCount: active.length, endedBy: isForced ? 'pot-limit' : 'showdown',
    });
    this.state.pot = 0;
    this.endRound();
    return showdown;
  }

  private checkPotLimit(): void {
    const s = this.state;
    if (s.potLimit && s.pot >= s.potLimit) {
      this.resolveShowdown(true);
    }
  }

  private endRoundByPack(winner: TeenPattiPlayerState, lastAction?: TeenPattiActionRecord): ActionResult {
    winner.chipStack += this.state.pot;
    this.state.roundHistory.push({
      roundNumber: this.state.roundNumber, winnerId: winner.id, winnerName: winner.name,
      potAmount: this.state.pot, playerCount: this.state.players.filter(p => p.status !== 'eliminated').length,
      endedBy: 'pack',
    });
    this.state.pot = 0;
    this.endRound();
    return { success: true, action: lastAction, roundComplete: true };
  }

  private endRound(): void {
    const s = this.state;
    // Eliminate players who can't afford boot
    for (const p of s.players) {
      if (p.status === 'active' && p.chipStack < s.bootAmount) {
        p.status = 'eliminated';
      }
      if (p.status === 'packed') {
        // Reset packed players to active for next round (if they have chips)
        if (p.chipStack >= s.bootAmount) {
          p.status = 'active';
        } else {
          p.status = 'eliminated';
        }
      }
    }
    const active = s.players.filter(p => p.status === 'active');
    if (active.length <= 1) {
      s.gameStatus = { type: 'complete', winnerId: active[0]?.id || '' };
    } else {
      s.gameStatus = { type: 'betweenRounds' };
    }
  }

  // =========================================================================
  // State queries
  // =========================================================================

  getGameState(): TeenPattiGameState {
    return { ...this.state };
  }

  getPlayerState(playerId: string): TeenPattiPlayerState | null {
    return this.findPlayer(playerId) || null;
  }

  getVisibleState(playerId: string): VisibleTeenPattiState {
    const s = this.state;
    const visiblePlayers: VisibleTeenPattiPlayerState[] = s.players.map(p => ({
      id: p.id, name: p.name, seatIndex: p.seatIndex, chipStack: p.chipStack,
      cards: p.id === playerId && p.blindSeenStatus === 'seen' ? [...p.cards] : null,
      status: p.status, blindSeenStatus: p.blindSeenStatus,
      currentBetThisRound: p.currentBetThisRound, isDealer: p.isDealer,
      hasActedThisRound: p.hasActedThisRound,
    }));
    return {
      tableSize: s.tableSize, buyIn: s.buyIn, bootAmount: s.bootAmount,
      potLimit: s.potLimit, players: visiblePlayers, dealerSeatIndex: s.dealerSeatIndex,
      roundNumber: s.roundNumber, pot: s.pot, currentStake: s.currentStake,
      currentPlayerIndex: s.currentPlayerIndex, actionHistory: [...s.actionHistory],
      pendingSideshow: s.pendingSideshow ? { ...s.pendingSideshow } : null,
      gameStatus: s.gameStatus,
    };
  }

  getPot(): number { return this.state.pot; }
  getCurrentStake(): number { return this.state.currentStake; }
  getDealerPosition(): number { return this.state.dealerSeatIndex; }

  getCurrentPlayer(): string | null {
    const p = this.state.players[this.state.currentPlayerIndex];
    return p?.status === 'active' ? p.id : null;
  }

  isRoundInProgress(): boolean {
    return this.state.gameStatus.type === 'roundInProgress' || this.state.gameStatus.type === 'sideshowPending';
  }

  isGameOver(): boolean {
    return this.state.gameStatus.type === 'complete';
  }

  getWinner(): string | null {
    return this.state.gameStatus.type === 'complete' ? this.state.gameStatus.winnerId : null;
  }

  getActivePlayers(): TeenPattiPlayerState[] {
    return this.state.players.filter(p => p.status === 'active');
  }

  getEliminatedPlayers(): TeenPattiPlayerState[] {
    return this.state.players.filter(p => p.status === 'eliminated');
  }

  // =========================================================================
  // Hand evaluation (delegates)
  // =========================================================================

  evaluateHand(cards: Card[]): TeenPattiHandResult {
    return HandEvaluator.evaluate(cards);
  }

  compareHands(hand1: TeenPattiHandResult, hand2: TeenPattiHandResult): number {
    return HandEvaluator.compare(hand1, hand2);
  }

  // =========================================================================
  // Serialization
  // =========================================================================

  serialize(): string {
    return JSON.stringify(this.state);
  }

  deserialize(json: string): TeenPattiGameState | { error: string } {
    try {
      const parsed = JSON.parse(json);
      if (!parsed || typeof parsed !== 'object') return { error: 'Invalid JSON: not an object' };
      if (!Array.isArray(parsed.players)) return { error: 'Invalid state: missing players array' };
      if (typeof parsed.pot !== 'number') return { error: 'Invalid state: missing pot' };
      if (typeof parsed.currentStake !== 'number') return { error: 'Invalid state: missing currentStake' };
      if (typeof parsed.roundNumber !== 'number') return { error: 'Invalid state: missing roundNumber' };
      if (!parsed.gameStatus || typeof parsed.gameStatus.type !== 'string') return { error: 'Invalid state: missing gameStatus' };
      this.state = parsed as TeenPattiGameState;
      return this.state;
    } catch (e) {
      return { error: `Deserialization failed: ${e instanceof Error ? e.message : String(e)}` };
    }
  }

  setGameState(state: TeenPattiGameState): void {
    this.state = state;
  }

  // =========================================================================
  // Helpers
  // =========================================================================

  private findPlayer(playerId: string): TeenPattiPlayerState | undefined {
    return this.state.players.find(p => p.id === playerId);
  }

  private recordAction(player: TeenPattiPlayerState, action: TeenPattiAction['type'], amount: number): TeenPattiActionRecord {
    const record: TeenPattiActionRecord = {
      playerId: player.id, playerName: player.name, action,
      amount, wasBlind: player.blindSeenStatus === 'blind', timestamp: Date.now(),
    };
    this.state.actionHistory.push(record);
    return record;
  }
}
